// packages
import { gql, DocumentNode } from '@apollo/client';
import { Box, Divider, Typography } from '@mui/material';
import React, { useCallback } from 'react';

// components
import OrganizationAvatar from '../../OrganizationAvatar';
import { InvoiceViewStatus } from './InvoiceViewStatus';

// logic
import { useDeviceDetection } from '../../../hooks/useDeviceDetection';
import {
	InvoiceStatus,
	InvoiceViewPresentationInvoiceFragment,
	TeamMemberRole,
	useInvoiceViewPresentation_GetSharableInvoiceLinkLazyQuery,
} from '#types';
import styles from './InvoiceViewPresentation.module.scss';
import { getFormattedDateWithTimestamp } from '../../../lib/helpers';
import {
	preferredDayJsDateFormatWithTimeStamp,
	preferredDayJsDateFormat,
} from '../../../lib/constants';
import { InvoiceViewBillTo } from './InvoiceViewBillTo';
import rpcShared from '@rockpapercoin/rpc-shared';
import { Status } from '../../Status';
import classNames from 'classnames';
import { CopySharableLink } from '../../CopySharableLink';
import { stringToClipboard } from '../../QuickPaymentsModal/helpers';
import { useToast } from '../../../hooks/useToast';
import { DocumentViewEmailNotificationGroup } from '../../DocumentViewEmailNotificationGroup';
import { CalendarLinks, CalendarSourceType } from '../../CalendarLinks';

const invoiceViewPresentationInvoiceFragment = gql`
	fragment invoiceViewPresentationInvoice on Invoice {
		id
		status
		isPastDue
		title
		sentAt
		eventDate
		uploadAttachment
		discountIsPercentage
		discountPercentage
		discountAmount
		organization {
			...organizationAvatarOrganization
		}
		contact {
			...getViewingUserDocumentRelationshipContact
		}
		...invoiceViewStatusInvoice
		...invoiceViewBillToInvoice
		...documentViewEmailNotificationGroupInvoice
	}
	${ InvoiceViewStatus.fragments.invoiceViewStatusInvoiceFragment }
	${ OrganizationAvatar.fragments.organizationAvatarOrganizationFragment }
	${ InvoiceViewBillTo.fragments.invoiceViewBillToInvoiceFragment }
	${ rpcShared.contacts.getViewingUserDocumentRelationshipContactFragment }
	${ DocumentViewEmailNotificationGroup.fragments
		.documentViewEmailNotificationGroupInvoiceFragment }
`;

const invoiceViewPresentationOrganizationFragment = gql`
	fragment invoiceViewPresentationOrganization on Organization {
		...organizationAvatarOrganization
	}
	${ OrganizationAvatar.fragments.organizationAvatarOrganizationFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewPresentationGetSharableInvoiceLinkQuery = gql`
	query invoiceViewPresentation_GetSharableInvoiceLink(
		$where: InvoiceWhereUniqueInput!
	) {
		getSharableInvoiceLink(where: $where)
	}
`;

export type InvoiceViewPresentationProps = {
	invoice?: InvoiceViewPresentationInvoiceFragment;
	requestingUserBaseId?: string;
};

type InvoiceViewPresentationPropsWithFragment =
	React.FC<InvoiceViewPresentationProps> & {
		fragments: {
			invoiceViewPresentationInvoiceFragment: DocumentNode;
			invoiceViewPresentationOrganizationFragment: DocumentNode;
		};
	};

export const InvoiceViewPresentation: InvoiceViewPresentationPropsWithFragment =
	( { invoice, requestingUserBaseId } ) => {
		// logic to divide the sections on mobile
		const { isMobile } = useDeviceDetection();

		const { showError, showSuccess } = useToast();

		// dates for the date formatting god
		const sentAtTimestamp = invoice?.sentAt
			? getFormattedDateWithTimestamp(
				invoice.sentAt,
				preferredDayJsDateFormatWithTimeStamp
			  )
			: '-';
		const eventDate = invoice?.eventDate
			? getFormattedDateWithTimestamp(
				invoice.eventDate,
				preferredDayJsDateFormat
			  )
			: undefined;

		const [ getSharableInvoiceLink, { loading } ] =
			useInvoiceViewPresentation_GetSharableInvoiceLinkLazyQuery( {
				variables: {
					where: {
						id: invoice?.id || '',
					},
				},
			} );

		const onClick = useCallback( () => {
			if ( invoice?.id ) {
				stringToClipboard( async () => {
					const res = await getSharableInvoiceLink();
					if ( !res ) {
						showError();
					} else if ( res.error ) {
						showError( res.error.message );
					} else if ( res.data?.getSharableInvoiceLink ) {
						showSuccess( 'Link copied to clipboard' );
						return res.data.getSharableInvoiceLink;
					}
				} );
			}
		}, [
			getSharableInvoiceLink,
			invoice?.id,
			showError,
			showSuccess
		] );

		const {
			viewingUserIsCustomer,
			viewingUserIsAPPA,
			viewingUserIsFromSendingOrganization,
		} =
			invoice?.contact && requestingUserBaseId
				? rpcShared.contacts.getViewingUserDocumentRelationship( {
					contact: invoice.contact,
					viewingUserId: requestingUserBaseId,
				  } )
				: {
					viewingUserIsCustomer: false,
					viewingUserIsAPPA: false,
					viewingUserIsFromSendingOrganization: false,
				  };

		const vendorOrgUser = invoice?.contact?.vendor?.users?.find(
			( user ) => user.user?.id === requestingUserBaseId
		);
		const isAdminManagerOrOwnerOfVendor = !!(
			vendorOrgUser?.role &&
			[
				TeamMemberRole.Owner,
				TeamMemberRole.Admin,
				TeamMemberRole.Manager,
			].includes( vendorOrgUser.role )
		);
		// Find the assigned member by cross-referencing OrgUser IDs in vendor users list
		const assignedMemberOrgUserId = invoice?.contact?.assignedMember?.id;
		const isAssignedMemberOfContact = !!(
			assignedMemberOrgUserId &&
			invoice?.contact?.vendor?.users?.some(
				( u ) =>
					u.id === assignedMemberOrgUserId &&
					u.user?.id === requestingUserBaseId
			)
		);
		const canSendCalendarInvite =
			isAdminManagerOrOwnerOfVendor || isAssignedMemberOfContact;

		const userFriendlyStatus = rpcShared.invoices.getUserFriendlyStatus(
			{
				status: invoice?.status || InvoiceStatus.Draft,
				isPastDue: invoice?.isPastDue || false,
				paymentInstallments: invoice?.paymentInstallments || [],
			},
			viewingUserIsCustomer || viewingUserIsAPPA
		);
		const contactOrganization = invoice?.organization || undefined;

		const MobileDivider = ( { className }: { className: string } ) =>
			isMobile ? (
				<Divider className={ classNames( styles.divider, className ) } />
			) : null;
		const MobileStatus = () =>
			isMobile && userFriendlyStatus ? (
				<Box className={ styles.status }>
					<Typography className={ styles.label }>Status</Typography>
					<Status status={ userFriendlyStatus } />
				</Box>
			) : null;

		return (
			<>
				<Box className={ styles.title }>
					<Typography variant='h1' data-cy='title' className={ styles.titleText }>
						{ invoice?.title }
					</Typography>
					{ userFriendlyStatus ? (
						<Status className={ styles.status } status={ userFriendlyStatus } />
					) : null }
				</Box>
				<MobileDivider className={ styles.divider1 } />
				<Box className={ styles.organizationAndSharableLink }>
					<OrganizationAvatar organization={ contactOrganization } />
					{ viewingUserIsFromSendingOrganization ? (
						<CopySharableLink
							onClick={ onClick }
							loading={ loading }
							data-cy='copy-sharable-invoice-link'
						/>
					) : null }
				</Box>
				<MobileDivider className={ styles.divider2 } />
				<MobileStatus />
				<Box
					className={ classNames(
						styles.dates,
						invoice?.eventDate ? styles.eventDate : undefined
					) }
				>
					<Typography
						className={ styles.label1 }
						gutterBottom
						data-cy='sent-label'
					>
						Sent on
					</Typography>
					<Typography className={ styles.value1 } data-cy='sent'>
						{ sentAtTimestamp }
					</Typography>
					{ invoice?.eventDate && invoice?.id ? (
						<>
							<Typography
								className={ styles.label2 }
								gutterBottom
								data-cy='eventDate-label'
							>
								Event date
							</Typography>
							<Box className={ styles.value2 }>
								<Typography data-cy='eventDate'>{ eventDate }</Typography>
								<CalendarLinks
									sourceType={ CalendarSourceType.INVOICE }
									sourceId={ invoice.id }
									iconOnly
									canSendCalendarInvite={
										canSendCalendarInvite &&
										!!invoice.contact?.customer?.email &&
										!!invoice.contact?.firstName
									}
									customerEmail={ invoice.contact?.customer?.email ?? undefined }
								/>
							</Box>
						</>
					) : null }
				</Box>
				<MobileDivider className={ styles.divider3 } />
				<InvoiceViewBillTo
					invoice={ invoice }
					className={ styles.contact }
					requestingUserBaseId={ requestingUserBaseId }
				/>
				<DocumentViewEmailNotificationGroup
					document={ invoice }
					className={ styles.emailNotificationGroup }
					isMobile={ isMobile }
				/>
			</>
		);
	};

InvoiceViewPresentation.fragments = {
	invoiceViewPresentationInvoiceFragment,
	invoiceViewPresentationOrganizationFragment,
};
